import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBookingStatus, useCancel, useAvailableSlots, useReschedule } from '../api/booking';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SlotPicker from '../components/SlotPicker/SlotPicker';
import type { Slot } from '../types/booking';

export default function StatusPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const { data, isLoading, refetch } = useBookingStatus(token || '');
  const { data: slotsData } = useAvailableSlots(token || '');
  const cancelMutation = useCancel(token || '');
  const rescheduleMutation = useReschedule(token || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data?.booking) {
    navigate(`/book/${token}`, { replace: true });
    return null;
  }

  const booking = data.booking;

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({ reason: cancelReason || undefined });
      await refetch();
      setShowCancel(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) return;

    try {
      await rescheduleMutation.mutateAsync({
        newSlotStart: selectedSlot.start,
        interviewerId: selectedSlot.interviewerId,
      });
      await refetch();
      setShowReschedule(false);
      setSelectedSlot(null);
    } catch {
      // Error handled by mutation
    }
  };

  const getStatusBadge = () => {
    switch (booking.status) {
      case 'CONFIRMED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Bestätigt
          </span>
        );
      case 'CANCELLED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Abgesagt
          </span>
        );
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Abgeschlossen
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {booking.status}
          </span>
        );
    }
  };

  return (
    <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold text-gray-900">Ihr Termin</h1>
            {getStatusBadge()}
          </div>

          {/* Booking Details */}
          <div className="space-y-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">Datum & Uhrzeit</p>
              <p className="font-medium text-gray-900">
                {new Date(booking.scheduledStart).toLocaleDateString('de-DE', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <p className="text-gray-700">
                {new Date(booking.scheduledStart).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                -{' '}
                {new Date(booking.scheduledEnd).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>

            {booking.interviewer && (
              <div>
                <p className="text-sm text-gray-500">Gesprächspartner</p>
                <p className="font-medium text-gray-900">{booking.interviewer.displayName}</p>
              </div>
            )}
          </div>

          {/* Teams Link */}
          {booking.teamsJoinUrl && booking.status === 'CONFIRMED' && (
            <a
              href={booking.teamsJoinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 mb-4"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.5 14.5v-8c0-.83-.67-1.5-1.5-1.5h-8c-.83 0-1.5.67-1.5 1.5v8c0 .83.67 1.5 1.5 1.5h8c.83 0 1.5-.67 1.5-1.5zm-3-6.5l2 1.5v5l-2 1.5v-8z" />
              </svg>
              Meeting beitreten
            </a>
          )}

          {/* Actions */}
          {data.canReschedule && data.canCancel && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReschedule(true)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Verschieben
              </button>
              <button
                onClick={() => setShowCancel(true)}
                className="flex-1 px-4 py-2 border border-red-300 rounded-md text-red-700 hover:bg-red-50"
              >
                Absagen
              </button>
            </div>
          )}
        </div>

        {/* Reschedule Modal */}
        {showReschedule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Termin verschieben</h2>

              {selectedSlot ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Neuer Termin:</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedSlot.start).toLocaleDateString('de-DE', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                    <p className="text-gray-700">
                      {new Date(selectedSlot.start).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>

                  {rescheduleMutation.error && (
                    <p className="text-sm text-red-600">
                      {(rescheduleMutation.error as Error).message}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedSlot(null)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md"
                    >
                      Zurück
                    </button>
                    <button
                      onClick={handleReschedule}
                      disabled={rescheduleMutation.isPending}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {rescheduleMutation.isPending ? 'Wird verschoben...' : 'Bestätigen'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {slotsData?.slots && slotsData.slots.length > 0 ? (
                    <SlotPicker slots={slotsData.slots} onSelect={setSelectedSlot} />
                  ) : (
                    <p className="text-gray-500 py-4">
                      Keine anderen Termine verfügbar.
                    </p>
                  )}
                </>
              )}

              <button
                onClick={() => {
                  setShowReschedule(false);
                  setSelectedSlot(null);
                }}
                className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Termin absagen</h2>
              <p className="text-gray-600 mb-4">
                Möchten Sie diesen Termin wirklich absagen?
              </p>

              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Grund für die Absage (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                rows={3}
              />

              {cancelMutation.error && (
                <p className="text-sm text-red-600 mb-4">
                  {(cancelMutation.error as Error).message}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancel(false);
                    setCancelReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelMutation.isPending ? 'Wird abgesagt...' : 'Termin absagen'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
